import { ResizeObserver } from "@juggle/resize-observer";
import { memo, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { DownloadPortalModal } from "~/components/blocks/product/common/blocks/download-portal-modal";
import { VideoLegacySeason } from "~/components/blocks/product/common/video-legacy-season";
import { VideoSeason } from "~/components/blocks/product/common/video-season";
import { VideoSpotLiveSeason } from "~/components/blocks/product/common/video-spot-live-season";
import { VideoStageSeason } from "~/components/blocks/product/common/video-stage-season";
import {
  isVideoLegacySeason,
  isVideoSeason,
  isVideoSpotLiveSeason,
  isVideoStageSeason,
} from "~/components/blocks/product/type-guard";
import { isFirstFocusPurchasedTabVar, modalReactiveWidthVar } from "~/components/stores/detail-product/store";
import { openTextOnlyToast } from "~/components/stores/toast/store";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { conversionApi } from "~/util/conversion-api";

type ProductProps = {
  /** `videoクエリ`のデータ取得中かどうかを判定するフラグ */
  isVideoLoading?: boolean;
  /** `videoクエリ`を叩いて取得したデータ */
  videoData?: G_GetVideoQuery;
};

/** プレイヤー以外のコンテンツを、/detailと/playerで共通して使用するコンポーネント */
export const Product = memo((props: ProductProps) => {
  const { videoData } = props;
  const detailRef = useRef<HTMLDivElement>(null);

  const { userId } = useAuth();
  const { replace, isReady } = useRouter();
  const { season } = useQueryString();

  useEffect(() => {
    if (season === undefined || !isReady) return;

    const currentUrl = new URL(location.href);
    const currentParams = currentUrl.searchParams;

    if (!currentParams.has("isPurchase")) return;
    isFirstFocusPurchasedTabVar(true);

    // @see https://confl.arms.dmm.com/pages/viewpage.action?pageId=1280311798
    void (async () => {
      if (userId === undefined) return;
      void conversionApi(userId, season);
    })();

    openTextOnlyToast("SUCCESS_PURCHASE");
    currentParams.delete("isPurchase");
    void replace(currentUrl);
  }, [season, isReady]);

  useEffect(() => {
    if (detailRef.current === null) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentBoxSize) {
        modalReactiveWidthVar(entry.contentBoxSize[0].inlineSize);
      } else {
        modalReactiveWidthVar(entry.contentRect.width);
      }
    });
    observer.observe(detailRef.current);
    modalReactiveWidthVar(detailRef.current.clientWidth);
    return () => {
      observer.disconnect();
    };
  }, [videoData]);

  if (videoData?.video === undefined) return null;

  return (
    <div ref={detailRef}>
      {isVideoSeason(videoData.video) && <VideoSeason videoSeasonData={videoData.video} />}

      {isVideoLegacySeason(videoData.video) && <VideoLegacySeason videoLegacySeasonData={videoData.video} />}

      {isVideoStageSeason(videoData.video) && <VideoStageSeason videoStageSeasonData={videoData.video} />}

      {isVideoSpotLiveSeason(videoData.video) && <VideoSpotLiveSeason videoSpotLiveSeasonData={videoData.video} />}

      <DownloadPortalModal />
    </div>
  );
});

Product.displayName = "Product";
